import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { delay, filter, map, switchMap } from 'rxjs/operators';
import { MOCK_ALLOCATION_S14_B11_100 } from '../mocks/mock-api.data.100s14b11';
import { MOCK_ALLOCATION_S13_B11_100 } from '../mocks/mock-api.data.100s13b11';
import { MOCK_ALLOCATION_S15_B11_100 } from '../mocks/mock-api.data.100s15b11';
import {
    buildMockAllocationS5B10,
    buildMockBenchmarkAllocationS5B10,
    MockRefData,
    mockDetailsUrl,
    mockDurationsUrl,
    mockInvestmentsUrl,
    mockMaturitiesUrl,
    patchMockImplementationChoice,
    MOCK_S5_B10_BENCHMARKS_URL,
    MOCK_S5_B10_IDS
} from '../mocks/mock-api.data.s5b10';

// TEMPORARY dev-only mock overrides. Add/remove entries in MOCK_OVERRIDES as needed.
// Only active when isDevMode() is true (ng serve), never in a production build.
const MOCK_OVERRIDES: { match: RegExp; response: any }[] = [
    // {
    //     match: /^https:\/\/data2\.ifa\.com\/api\/v1\/implementations\/allocation\/s13-b11-100$/,
    //     response: MOCK_ALLOCATION_S13_B11_100
    // },
    // {
    //     match: /^https:\/\/data2\.ifa\.com\/api\/v1\/implementations\/allocation\/s14-b11-100$/,
    //     response: MOCK_ALLOCATION_S14_B11_100
    // },
    //  {
    //     match: /^https:\/\/data2\.ifa\.com\/api\/v1\/implementations\/allocation\/s15-b11-100$/,
    //     response: MOCK_ALLOCATION_S15_B11_100
    // },
];

// Any portfolio number: s5-b10-0 through s5-b10-100.
const S5_B10_ALLOCATION_URL = /^https:\/\/data2\.ifa\.com\/api\/v1\/implementations\/allocation\/s5-b10-(\d+)$/;
const S5_B10_BENCHMARK_URL = /^https:\/\/data2\.ifa\.com\/api\/v1\/implementations\/benchmark-allocation\/s5-b10-(\d+)\/(\d+)$/;
const allocationUrlFor = (portfolioNumber: number) =>
    `https://data2.ifa.com/api/v1/implementations/allocation/s5-b10-${portfolioNumber}`;

@Injectable()
export class MockApiInterceptor implements HttpInterceptor {

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (isDevMode() && request.method === 'GET') {

            // ---------------------------------------------------------------
            // DISABLED: s5-b10-* mock. The new S5B10 lineup is live in the
            // database, so the real API now returns it and no interception is
            // needed. Uncomment this whole block to mock the series again -
            // the imports, URL patterns and helpers below are all still here.
            // ---------------------------------------------------------------
            //const allocationMatch = S5_B10_ALLOCATION_URL.exec(request.url);
            //if (allocationMatch) {
            //    const portfolioNumber = Number(allocationMatch[1]);
            //    return forkJoin({
            //        allocation: this.get(next, request),
            //        ref: this.refData(next, MOCK_S5_B10_IDS)
            //    }).pipe(map(({ allocation, ref }) => {
            //        console.warn('MockApiInterceptor: serving live API fund data with MOCK weights for', request.url);
            //        return allocation.clone({
            //            body: buildMockAllocationS5B10(allocation.body, ref, portfolioNumber)
            //        });
            //    }));
            //}

            //const benchmarkMatch = S5_B10_BENCHMARK_URL.exec(request.url);
            //if (benchmarkMatch) {
            //    const portfolioNumber = Number(benchmarkMatch[1]);
            //    const benchmarkTypeId = Number(benchmarkMatch[2]);
            //    // Read the chosen benchmarks first - their ids decide which extra
            //    // investments the reference lookups have to cover.
            //    return this.getBody<any[]>(next, MOCK_S5_B10_BENCHMARKS_URL).pipe(
            //        switchMap(mappings => {
            //            const benchmarkIds = mappings
            //                .filter(m => m.benchmark_type_id === benchmarkTypeId)
            //                .map(m => m.benchmark_id);
            //            const ids = Array.from(new Set([...MOCK_S5_B10_IDS, ...benchmarkIds]));
            //            return forkJoin({
            //                benchmark: this.get(next, request),
            //                allocation: this.getBody<any>(next, allocationUrlFor(portfolioNumber)),
            //                ref: this.refData(next, ids)
            //            }).pipe(map(({ benchmark, allocation, ref }) => {
            //                console.warn('MockApiInterceptor: serving MOCK benchmark allocation for', request.url);
            //                const mocked = buildMockAllocationS5B10(allocation, ref, portfolioNumber);
            //                return benchmark.clone({
            //                    body: buildMockBenchmarkAllocationS5B10(
            //                        benchmark.body, mocked, benchmarkTypeId, mappings, ref)
            //                });
            //            }));
            //        })
            //    );
            //}

            //// The mix page header reads strategy / fund_type_name from these lists.
            //if (request.url === 'https://data2.ifa.com/api/v1/implementations/equity') {
            //    return this.get(next, request).pipe(map(response => response.clone({
            //        body: patchMockImplementationChoice(response.body, 5, 'equity')
            //    })));
            //}
            //if (request.url === 'https://data2.ifa.com/api/v1/implementations/fixedincome') {
            //    return this.get(next, request).pipe(map(response => response.clone({
            //        body: patchMockImplementationChoice(response.body, 10, 'fixedincome')
            //    })));
            //}

            const override = MOCK_OVERRIDES.find(o => o.match.test(request.url));
            if (override) {
                console.warn('MockApiInterceptor: serving MOCK response for', request.url);
                return of(new HttpResponse({ status: 200, body: override.response })).pipe(delay(100));
            }
        }
        return next.handle(request);
    }

    private get(next: HttpHandler, request: HttpRequest<unknown> | string): Observable<HttpResponse<any>> {
        const req = typeof request === 'string' ? new HttpRequest<unknown>('GET', request) : request;
        return next.handle(req).pipe(
            filter((event): event is HttpResponse<any> => event instanceof HttpResponse)
        );
    }

    private getBody<T>(next: HttpHandler, url: string): Observable<T> {
        return this.get(next, url).pipe(map(response => response.body as T));
    }

    private refData(next: HttpHandler, ids: number[]): Observable<MockRefData> {
        return forkJoin({
            details: this.getBody<any[]>(next, mockDetailsUrl(ids)),
            investments: this.getBody<any[]>(next, mockInvestmentsUrl(ids)),
            durations: this.getBody<any[]>(next, mockDurationsUrl(ids)),
            maturities: this.getBody<any[]>(next, mockMaturitiesUrl(ids))
        });
    }
}
